import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { routerSegments } from '@consts/routes';
import { features } from '@consts/text.const';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrgVariablesModel } from '@models/org-variables.model';
import { OrgVariablesActions } from '@states/org-variables/org-variables.action-types';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiNoDataComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { OrgVariableDialogComponent } from '../../../../../pages/database/pages/variables/dialogs/org-variable-dialog/org-variable-dialog.component';
import { UiSearchComponent } from '../../../../ui-kit/ui-search/ui-search.component';
import { ViewStructureModalComponent } from '../../../view-structure-modal/view-structure-modal.component';
import {
  VariableResultCellContext,
  createVariablesResultTableConfig,
} from './variables-result-table.util';

const JSON_VARIABLE_TYPE = 'json';

@Component({
  selector: 'app-variables-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiSearchComponent,
  ],
  templateUrl: './variables-result.component.html',
  styleUrls: ['./variables-result.component.scss'],
})
export class VariablesResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly store$ = inject(Store);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);

  protected readonly noData = features.variables.noData;
  protected readonly noResults = features.variables.noResults;
  protected readonly typeCellTemplate = viewChild<TemplateRef<VariableResultCellContext>>('typeCell');
  protected readonly valueCellTemplate = viewChild<TemplateRef<VariableResultCellContext>>('valueCell');
  protected readonly searchQuery = signal<string>('');

  protected readonly response: Signal<IntelligenceModels.Variables.Response | null> = computed(() => {
    const result = this.resultsService.current();
    return result?.type === 'variables' ? (result.data as IntelligenceModels.Variables.Response) : null;
  });

  protected readonly hasSourceVariables = computed(() => (this.response()?.variables.length ?? 0) > 0);

  protected readonly variables: Signal<IntelligenceModels.Variables.Variable[]> = computed(() => {
    const all = this.response()?.variables ?? [];
    const query = this.searchQuery().trim().toLowerCase();
    return query ? all.filter((variable) => variable.name.toLowerCase().includes(query)) : all;
  });

  protected readonly tableConfig: Signal<DatasetTableConfig<IntelligenceModels.Variables.Variable> | null> =
    computed(() => {
      const typeCellTemplate = this.typeCellTemplate();
      const valueCellTemplate = this.valueCellTemplate();
      return typeCellTemplate && valueCellTemplate
        ? createVariablesResultTableConfig({ typeCellTemplate, valueCellTemplate })
        : null;
    });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'VARIABLES_RESULT.OPEN_PAGE',
        onClick: () => this.openVariablesPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  protected formatVariableType(type: string): string {
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
  }

  protected isJsonVariable(variable: IntelligenceModels.Variables.Variable): boolean {
    return variable.type === JSON_VARIABLE_TYPE;
  }

  protected viewJson(value: unknown): void {
    this.dialog.open(ViewStructureModalComponent, {
      panelClass: 'thumbnail-dialog-wrapper',
      width: '80vw',
      height: '600px',
      data: value,
    });
  }

  protected createVariable(): void {
    this.dialog
      .open(OrgVariableDialogComponent, { width: '400px', panelClass: 'modal-no-padding' })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((variable: OrgVariablesModel.Variable | undefined) => {
        if (variable) this.store$.dispatch(OrgVariablesActions.addVariable({ variable }));
      });
  }

  protected onRowAction(event: IntelligenceModels.Variables.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case IntelligenceModels.Variables.RowActionId.Edit:
        this.editVariable(event.row);
        break;
      case IntelligenceModels.Variables.RowActionId.Delete:
        this.removeVariable(event.row.name);
        break;
    }
  }

  private editVariable(variable: IntelligenceModels.Variables.Variable): void {
    this.dialog
      .open(OrgVariableDialogComponent, {
        width: '400px',
        panelClass: 'modal-no-padding',
        data: variable as OrgVariablesModel.Variable,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((updatedVariable: OrgVariablesModel.Variable | undefined) => {
        if (updatedVariable) this.store$.dispatch(OrgVariablesActions.editVariable({ variable: updatedVariable }));
      });
  }

  private removeVariable(name: string): void {
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translate.instant('DELETE_VARIABLE_MODAL.TITLE'),
        msg: this.translate.instant('DELETE_VARIABLE_MODAL.MSG', { name }),
        confirm: this.translate.instant('CONFIRM_MODALS.COMMON_YES'),
        cancel: this.translate.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store$.dispatch(OrgVariablesActions.removeVariable({ name })));
  }

  private openVariablesPage(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, routerSegments.variables]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
